import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

const PageEdit = ({ pageType }) => {
  const { pageId: paramPageId } = useParams();
  const pageId = paramPageId || pageType;
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (pageId) {
      fetchPage(pageId);
    }
  }, [pageId]);

  const fetchPage = async (id) => {
    try {
      const response = await apiService.getPage(id);
      if (response.success) {
        setPage(response.data.page);
      }
    } catch (err) {
      // If page not found and we have a pageType, auto-create it
      if (pageType && err.message && err.message.includes('not found')) {
        try {
          const createResponse = await apiService.createPage({
            pageId: pageType,
            title: pageType.charAt(0).toUpperCase() + pageType.slice(1),
            content: { hero: { title: '', subtitle: '', backgroundImage: '' } },
            seoMeta: { metaTitle: '', metaDescription: '', keywords: [] },
            isPublished: true
          });
          if (createResponse.success) {
            setPage(createResponse.data.page);
            return;
          }
        } catch (createErr) {
          setError('Failed to initialize page');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!page || !pageId) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await apiService.updatePage(pageId, page);
      setSuccess('Page updated successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update page');
    } finally {
      setSaving(false);
    }
  };

  const updatePageField = (field, value) => {
    setPage(prev => prev ? { ...prev, [field]: value } : null);
  };

  const updateHeroField = (field, value) => {
    setPage(prev => prev ? {
      ...prev,
      content: {
        ...prev.content,
        hero: {
          ...prev.content.hero,
          [field]: value
        }
      }
    } : null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">
          <i className="fas fa-spinner fa-spin mr-2"></i>
          Loading page...
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="text-center py-8">
        <i className="fas fa-exclamation-circle text-4xl text-red-300 mb-4"></i>
        <p className="text-red-600">Page not found.</p>
        <button 
          onClick={() => navigate('/admin/pages')}
          className="btn btn-primary mt-4"
        >
          Back to Pages
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Edit Page: {page.pageId}
        </h1>
        <button
          onClick={() => navigate('/admin/pages')}
          className="btn btn-secondary"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Pages
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success mb-4">
          {success}
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="card mb-6">
              <div className="card-header">
                <h3 className="card-title">Basic Information</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Page Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={page.title}
                    onChange={(e) => updatePageField('title', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Hero Section */}
            <div className="card mb-6">
              <div className="card-header">
                <h3 className="card-title">Hero Section</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Hero Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={page.content.hero?.title || ''}
                    onChange={(e) => updateHeroField('title', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hero Subtitle</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={page.content.hero?.subtitle || ''}
                    onChange={(e) => updateHeroField('subtitle', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Background Image URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={page.content.hero?.backgroundImage || ''}
                    onChange={(e) => updateHeroField('backgroundImage', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
            </div>

            {/* SEO Meta */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">SEO Meta</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={page.seoMeta?.metaTitle || ''}
                    onChange={(e) => updatePageField('seoMeta', {
                      ...page.seoMeta,
                      metaTitle: e.target.value
                    })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={page.seoMeta?.metaDescription || ''}
                    onChange={(e) => updatePageField('seoMeta', {
                      ...page.seoMeta,
                      metaDescription: e.target.value
                    })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Keywords (comma-separated)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={page.seoMeta?.keywords?.join(', ') || ''}
                    onChange={(e) => updatePageField('seoMeta', {
                      ...page.seoMeta,
                      keywords: e.target.value.split(',').map(k => k.trim())
                    })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Page Status</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      checked={page.isPublished}
                      onChange={(e) => updatePageField('isPublished', e.target.checked)}
                      className="mr-2"
                    />
                    Published
                  </label>
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Last Modified:</strong></p>
                  <p>{new Date(page.lastModified).toLocaleString()}</p>
                  <p><strong>By:</strong> {page.modifiedBy?.username}</p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary w-full"
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PageEdit;